import {
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
} from "@chakra-ui/react"
import { Button } from "components/Button"
import { Link } from "components/Link"
import type { PurchaseType } from "components/Shop/PDPPage/types"
import { CloseIcon } from "components/SvgIcons"
import { getCancellationLink } from "config/urls"
import { useLocale } from "hooks"
import { colours } from "@shared/ui.design-system"
import React from "react"
import { fonts } from "theme/themeConfig"
import { useSegment } from "tracking"
import type { MembershipOptionsV2 } from "types"

type Props = {
  isOpen?: boolean
  onClose: () => void
  onAddToBag: ({
    includeAccessPass,
    purchaseTypeIdentifier,
    membershipOverride,
  }: {
    includeAccessPass: boolean
    purchaseTypeIdentifier: PurchaseType
    membershipOverride?: MembershipOptionsV2 | undefined
  }) => void
}

export const PricingModal: React.FC<Props> = ({
  onAddToBag,
  onClose,
  isOpen = false,
}) => {
  const { track } = useSegment()
  const locale = useLocale()

  return (
    <Modal
      data-testid="ATBModal"
      isOpen={isOpen}
      onClose={onClose}
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent
        containerProps={{
          height: "100vh",
          alignItems: { base: "flex-end", lg: "center" },
        }}
        data-testid="ATBModalContent"
        maxWidth={{ base: "100%", lg: "530px" }}
        borderRadius={0}
        mb={{ base: 0, lg: "initial" }}
        px={{ base: "24px", md: "80px", lg: "32px" }}
        pt={{ base: "24px", md: "40px", lg: "32px" }}
        pb={{ base: "74px", md: "40px", lg: "32px" }}
      >
        <ModalHeader p={{ base: 0 }} data-testid="ATBModalHeader">
          <Text variant="apercu" fontSize="28px" lineHeight="30.8px">
            Continue without Free Trial?
          </Text>
          <CloseIcon
            cursor="pointer"
            data-testid="access-pass-modal-close"
            onClick={() => {
              track({
                event: "Page Element Clicked",
                properties: {
                  module_content: "Continue without Access Pass (x)",
                },
              })

              onClose()
            }}
            fill={colours.primary.dark}
            boxSize={{ base: "30px", md: "40px", lg: "30px" }}
            position={{ base: "absolute" }}
            top={{ base: "20px", md: "50px", lg: "40px" }}
            right={{ base: "14px", md: "80px", lg: "40px" }}
          />
        </ModalHeader>
        <ModalBody data-testid="ATBModalBody" p={0} py="16px">
          <UnorderedList color={colours.primary.medium} variant="acuminLight">
            <ListItem>
              <Text as="span" textTransform="uppercase">
                free
              </Text>{" "}
              Access to member pricing - no catch - cancel any time within the
              60 day period
            </ListItem>
            <ListItem>
              Guilt-Free Splurging (and up to four times more great beauty
              products for your money)
            </ListItem>
            <ListItem>
              Access to shop direct for amazing products from leading
              skincare/makeup/wellness labs
            </ListItem>
            <ListItem>
              Beauty Pie Member Prices - up to 70% off Typical Retail*
            </ListItem>
          </UnorderedList>

          <Text variant="acumin">Membership automatically renews.</Text>
          <Link
            href={getCancellationLink(locale)}
            textDecor="underline"
            variant="acumin"
            fontFamily={fonts.acuminLight}
          >
            Cancellation information here.
          </Link>
          <Button
            width="full"
            variant="secondary"
            fontFamily={fonts.acuminMedium}
            fontWeight="500"
            fontSize="20px"
            onClick={() => {
              track({
                event: "Page Element Clicked",
                properties: {
                  module_content: "Don’t add Access Pass",
                },
              })
              onClose()
              onAddToBag({
                includeAccessPass: false,
                purchaseTypeIdentifier: "non-member",
              })
            }}
          >
            Don’t add Free Trial
          </Button>
          <Button
            width="full"
            variant="primary"
            fontFamily={fonts.acuminMedium}
            fontWeight="500"
            fontSize="20px"
            onClick={() => {
              track({
                event: "Page Element Clicked",
                properties: {
                  module_content: "Add Access Pass",
                },
              })
              onClose()
              onAddToBag({
                includeAccessPass: true,
                purchaseTypeIdentifier: "member",
              })
            }}
          >
            Add Free Trial
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
